import React, { useState, useEffect, useRef } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Pagination from "react-bootstrap/Pagination";
import { IoIosSearch } from "react-icons/io";
import Loading from "../loader/Loading";
import { getTenantListWithMetrics } from "../../context/functions/AdminFunction";
import { toast } from "react-toastify";
import moment from "moment";
import "../../styles/tenants.css";
import { constants } from "../../utils/constants";

const TenantListWithMetrics = ({ externalFilters = {} }) => {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    page: 1,
    limit: constants.LISTING_ROWLIMIT,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    clave_done: "",
    bank_status: "",
    profile_completed: "",
  });

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const latestSearchRef = useRef(filters.search);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    latestSearchRef.current = filters.search;
  }, [filters.search]);

  useEffect(() => {
    loadTenants();
  }, [pagination.page]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    loadTenants(controller.signal);
  }, [
    externalFilters.nationalities,
    externalFilters.employmentStatuses,
    externalFilters.landlordIds,
  ]);

  const loadTenants = async (signal = null) => {
    setLoading(true);
    try {
      const response = await getTenantListWithMetrics({
        page: pagination.page,
        limit: pagination.limit,
        search: latestSearchRef.current,
        clave_done: filters.clave_done,
        bank_status: filters.bank_status,
        profile_completed: filters.profile_completed,
        nationality: externalFilters.nationalities || [],
        employment_status: externalFilters.employmentStatuses || [],
        landlord_id: externalFilters.landlordIds || [],
        signal,
      });

      if (signal?.aborted) return;

      if (response && response.success && response.data) {
        setTenants(response.data.records || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: response.data.pagination?.totalRecords || 0,
          totalPages: response.data.pagination?.totalPages || 0,
        }));
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED" || error.name === "AbortError") {
        console.log("Request canceled");
        return;
      }
      console.error("Failed to load tenant list with metrics", error);
      toast.error("Failed to load tenant list");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === "search") {
      setPagination((prev) => ({ ...prev, page: 1 }));

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        loadTenants(controller.signal);
      }, 500);
    } else {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const handleApplyFilters = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    loadTenants(controller.signal);
  };

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => setPagination((prev) => ({ ...prev, page: 1 }))}
        disabled={pagination.page === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
        disabled={pagination.page === 1}
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.page}
          onClick={() => setPagination((prev) => ({ ...prev, page }))}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
        disabled={pagination.page === pagination.totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagination((prev) => ({ ...prev, page: pagination.totalPages }))}
        disabled={pagination.page === pagination.totalPages}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> to{" "}
          <strong>{Math.min(pagination.page * pagination.limit, pagination.totalRecords)}</strong> of{" "}
          <strong>{pagination.totalRecords}</strong> tenants
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <div className="tenants-container metrics-theme-table">
      <div className="tenants-card">
        <div className="p-4 pb-3">
          <h5 className="fw-bold mb-3 metrics-table-title">
            Tenant List with Metrics
          </h5>

          <div className="tenants-filter-row">
            <div className="tenants-filter-item-wide">
              <label className="tenants-filter-label">Search</label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="tenants-search-input"
                />
                <InputGroup.Text className="tenants-search-group-text">
                  <IoIosSearch />
                </InputGroup.Text>
              </InputGroup>
            </div>

            <div className="tenants-filter-item">
              <label className="tenants-filter-label">Clave Done</label>
              <Form.Select
                value={filters.clave_done}
                onChange={(e) =>
                  handleFilterChange("clave_done", e.target.value)
                }
                style={{ borderRadius: "6px", height: "38px" }}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </div>

            <div className="tenants-filter-item">
              <label className="tenants-filter-label">Bank Status</label>
              <Form.Select
                value={filters.bank_status}
                onChange={(e) =>
                  handleFilterChange("bank_status", e.target.value)
                }
                style={{ borderRadius: "6px", height: "38px" }}
              >
                <option value="">All</option>
                <option value="true">Linked</option>
                <option value="false">Not Linked</option>
              </Form.Select>
            </div>

            <div className="tenants-filter-item">
              <label className="tenants-filter-label">Profile Completed</label>
              <Form.Select
                value={filters.profile_completed}
                onChange={(e) =>
                  handleFilterChange("profile_completed", e.target.value)
                }
                style={{ borderRadius: "6px", height: "38px" }}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </div>

            <Button className="tenants-btn-apply" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <Loading />
          ) : (
            <Table responsive hover className="tenants-table mb-0">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th>CLAVE DONE</th>
                  <th>BANK STATUS</th>
                  <th>PROFILE COMPLETED</th>
                  <th>DOCS UPLOADED</th>
                  <th>JOINED ON</th>
                  <th>VERIFIED</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length > 0 ? (
                  tenants.map((tenant, index) => (
                    <tr key={tenant._id || index}>
                      <td>
                        <div className="tenant-name-stack">
                          <span className="tenant-name">
                            {tenant.fname} {tenant.lname}
                          </span>
                        </div>
                      </td>
                      <td className="fw-500">{tenant.email || "-"}</td>
                      <td className="fw-500">{tenant.phone_number || "-"}</td>
                      <td>
                        {tenant.clave_done ? (
                          <span className="tenant-kyc-yes">Yes</span>
                        ) : (
                          <span className="tenant-kyc-no">No</span>
                        )}
                      </td>
                      <td>
                        {tenant.bank_status ? (
                          <span className="tenant-kyc-yes">Linked</span>
                        ) : (
                          <span className="tenant-kyc-no">Not Linked</span>
                        )}
                      </td>
                      <td>
                        {tenant.profile_completed ? (
                          <span className="tenant-status-active">Yes</span>
                        ) : (
                          <span className="tenant-status-inactive">No</span>
                        )}
                      </td>
                      <td className="text-center fw-600">
                        {tenant.total_docs || 0}
                      </td>
                      <td className="text-muted fw-medium small">
                        {tenant.created_at
                          ? moment(tenant.created_at).format("DD MMM YYYY")
                          : "-"}
                      </td>
                      <td>
                        {tenant.verified ? (
                          <span className="tenant-kyc-yes">Yes</span>
                        ) : (
                          <span className="tenant-kyc-no">No</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      No Data Found!
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="p-3">
          {pagination.totalPages > 1 &&
            !loading &&
            tenants.length > 0 &&
            renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default TenantListWithMetrics;
